import React from "react";
import { Link } from "react-router-dom";
import SEO from "./SEO";

const ServiceLayout = ({ 
  title, 
  subtitle, 
  heroImage,
  children,
  whatsIncluded = [],
  perfectFor = [],
  pricing = [],
  features = [],
  seoTitle,
  seoDescription,
  seoKeywords = "",
  canonicalUrl,
  ogImage,
  structuredData
}) => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title={seoTitle || title}
        description={seoDescription || subtitle}
        keywords={seoKeywords}
        canonicalUrl={canonicalUrl}
        ogImage={ogImage || heroImage}
        ogType="website"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://theofficemealco.com/" },
              { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://theofficemealco.com/#services" },
              { "@type": "ListItem", "position": 3, "name": title, "item": canonicalUrl }
            ]
          },
          ...(structuredData ? (Array.isArray(structuredData) ? structuredData : [structuredData]) : [])
        ]}
      />
      {/* Navigation Breadcrumb - Styled like your website */}
      <div className="bg-[#F3EFE6] py-4 px-6 md:px-12 lg:px-20 border-b border-[#E8DDCB]">
        <div className="max-w-7xl mx-auto flex items-center text-sm text-[#64748B] font-sans">
          <Link to="/" className="hover:text-[#1E3A5F] transition-colors">Home</Link>
          <span className="mx-2 text-[#CBA365]">/</span>
          <a href="/#services" className="hover:text-[#1E3A5F] transition-colors">Services</a>
          <span className="mx-2 text-[#CBA365]">/</span>
          <span className="text-[#1E3A5F] font-medium">{title}</span>
        </div>
      </div>

      {/* Hero Section - Matching your website style */}
      <section className="relative py-16 md:py-24 px-6 md:px-12 lg:px-20 overflow-hidden bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div className="order-2 lg:order-1">
              <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
                Premium Service
              </span>

              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1E3A5F] mb-6 leading-[1.02]">
                {title}
              </h1>

              <p className="text-lg text-[#64748B] leading-relaxed mb-8 max-w-xl font-sans">
                {subtitle}
              </p>

              <div className="flex flex-wrap gap-4">
                <a 
                  href="/#contact" 
                  className="inline-flex items-center gap-2 bg-[#1E3A5F] text-[#FDFBF7] px-8 py-4 font-medium hover:bg-[#122644] transition-colors font-sans"
                >
                  Request a Quote
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </a>

                <a 
                  href="/#menu" 
                  className="inline-flex items-center gap-2 border border-[#1E3A5F] text-[#1E3A5F] px-8 py-4 font-medium hover:bg-[#1E3A5F] hover:text-[#FDFBF7] transition-colors font-sans"
                >
                  Explore Menu
                </a>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#E8DDCB] rounded-full opacity-30 blur-3xl"></div>
                <img 
                  src={heroImage || "/images/default-service.jpg"} 
                  alt={title}
                  className="relative w-full h-[400px] md:h-[500px] object-cover shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Content Section (passed from individual pages) */}
      {children && (
        <section className="py-16 px-6 md:px-12 lg:px-20 bg-[#FDFBF7]">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </section>
      )}

      {/* What's Included Section */}
      {whatsIncluded.length > 0 && (
        <section className="py-16 px-6 md:px-12 lg:px-20 bg-white">
          <div className="max-w-7xl mx-auto">
            <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
              01 • What's Included
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A5F] mb-12">
              Everything taken care of
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whatsIncluded.map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-6 border border-[#E8DDCB] hover:border-[#CBA365] transition-colors bg-[#FDFBF7]">
                  <div className="w-10 h-10 bg-[#F3EFE6] flex items-center justify-center flex-shrink-0">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-[#1E3A5F]">
                      <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1E3A5F] mb-1 font-sans">{item.title}</h3>
                    <p className="text-sm text-[#64748B] font-sans">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Perfect For Section */}
      {perfectFor.length > 0 && (
        <section className="py-16 px-6 md:px-12 lg:px-20 bg-[#FDFBF7]">
          <div className="max-w-7xl mx-auto">
            <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
              02 • Perfect For
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A5F] mb-12">
              Ideal occasions
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {perfectFor.map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-[#F3EFE6] border-l-4 border-[#CBA365]">
                  <span className="font-serif text-2xl text-[#CBA365]">{String(index + 1).padStart(2, '0')}</span>
                  <span className="text-[#1E3A5F] font-sans">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      {pricing.length > 0 && (
        <section className="py-16 px-6 md:px-12 lg:px-20 bg-white">
          <div className="max-w-7xl mx-auto">
            <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
              03 • Pricing
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E3A5F] mb-12">
              Transparent pricing
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              {pricing.map((item, index) => (
                <div key={index} className="border border-[#E8DDCB] p-8 hover:border-[#1E3A5F] transition-colors bg-[#FDFBF7]">
                  <h3 className="font-serif text-xl text-[#1E3A5F] mb-2">{item.name}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="font-serif text-3xl text-[#1E3A5F]">{item.price}</span>
                    <span className="text-[#64748B] text-sm font-sans">{item.unit}</span>
                  </div>
                  <p className="text-[#64748B] text-sm mb-6 font-sans">{item.description}</p>
                  <ul className="space-y-2">
                    {item.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2 text-sm text-[#64748B] font-sans">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M11.6667 3.5L5.25004 9.91667L2.33337 7" stroke="#CBA365" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Grid Section */}
      {features.length > 0 && (
        <section className="py-16 px-6 md:px-12 lg:px-20 bg-[#FDFBF7]">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-white p-6 shadow-sm text-center hover:shadow-md transition-shadow border-t-4 border-[#CBA365]">
                  {feature.icon && (
                    <div className="w-12 h-12 bg-[#F3EFE6] flex items-center justify-center mx-auto mb-4">
                      {feature.icon}
                    </div>
                  )}
                  <h3 className="font-serif text-lg text-[#1E3A5F] mb-2">{feature.title}</h3>
                  <p className="text-sm text-[#64748B] font-sans">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Matching your website's dark sections */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-[#1E3A5F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-[#CBA365] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#CBA365] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative">
          <span className="text-[#CBA365] text-xs tracking-[0.24em] uppercase font-medium mb-4 block font-sans">
            Get Started
          </span>

          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#FDFBF7] mb-6">
            Ready to elevate your<br/>office dining experience?
          </h2>

          <p className="text-[#E8DDCB] mb-10 max-w-xl mx-auto font-sans">
            We respond to every enquiry within one business day with a bespoke proposal tailored to your team size and requirements.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/#contact" 
              className="inline-flex items-center gap-2 bg-[#CBA365] text-[#1E3A5F] px-8 py-4 font-medium hover:bg-[#E8DDCB] transition-colors font-sans"
            >
              Request a Quote
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            </a>

            <a 
              href="tel:+919482415325" 
              className="inline-flex items-center gap-2 border border-[#E8DDCB] text-[#E8DDCB] px-8 py-4 font-medium hover:bg-[#E8DDCB] hover:text-[#1E3A5F] transition-colors font-sans"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14.5 11.5V13.5C14.5 14.0523 14.0523 14.5 13.5 14.5C7.70101 14.5 3 9.79899 3 4C3 3.44772 3.44772 3 4 3H6C6.55228 3 7 3.44772 7 4C7 5.15209 7.23158 6.24866 7.65286 7.25376C7.74308 7.47512 7.68898 7.72962 7.51777 7.90083L6.29289 9.12571C7.48013 11.3026 9.69736 13.5199 11.8743 14.7071L13.0992 13.4822C13.2704 13.311 13.5249 13.2569 13.7462 13.3471C14.7513 13.7684 15.8479 14 17 14C17.5523 14 18 14.4477 18 15V17C18 17.5523 17.5523 18 17 18H15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Call Us
            </a>
          </div>
        </div>
      </section>

      {/* Other Services Navigation */}
      <section className="py-16 px-6 md:px-12 lg:px-20 bg-[#F3EFE6]">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-serif text-2xl text-[#1E3A5F] mb-8 text-center">Explore Other Services</h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { path: "/daily-corporate-meals", name: "Daily Corporate Meals", active: title === "Daily Corporate Meals" },
              { path: "/event-catering", name: "Event Catering", active: title === "Event Catering" },
              { path: "/bento-boxes", name: "Bento Boxes", active: title === "Bento Boxes" },
              { path: "/pantry-management", name: "Pantry Management", active: title === "Pantry Management" },
            ].map((service) => (
              <Link 
                key={service.path}
                to={service.path}
                className={`p-6 text-center transition-colors font-sans ${
                  service.active 
                    ? 'bg-[#1E3A5F] text-[#FDFBF7]' 
                    : 'bg-white text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-[#FDFBF7]'
                }`}
              >
                <span className="font-medium">{service.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceLayout;
